// frontend/src/components/Layout.jsx
import React, { Fragment, useMemo } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { Menu, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  UserCircleIcon,
  BuildingOffice2Icon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import { getUsage } from "../api/orgs";
import { planId, hasFeature, tierForFeature } from "../lib/planMeta";
import Logo from "../marketing/Logo";
import PlanBadge from "./PlanBadge";
import { LockChip } from "./FeatureGate";
import NotificationBell from "./NotificationBell";
import OrgSwitcher from "./OrgSwitcher";
import FolderTree from "./FolderTree";
import ChatWidget from "./ChatWidget";
import ReConsentPrompt from "./ReConsentPrompt";

/** Initials for the avatar — from display name or email local-part. */
function initials(nameOrEmail) {
  const src = String(nameOrEmail || "").trim();
  if (!src) return "?";
  if (src.includes("@")) return src[0].toUpperCase();
  const parts = src.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

/** Icon — small inline SVG wrapper to keep JSX readable. */
function Icon({ path, className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}
Icon.propTypes = {
  path: PropTypes.string.isRequired,
  className: PropTypes.string,
};
Icon.defaultProps = { className: "w-5 h-5" };

// Individual nav links. `lockFeature` is the pricing.js comparison row a link
// needs — a lock chip appears beside it when the plan lacks it (PA1-T4).
const LINK_DASHBOARD = {
  to: "/dashboard",
  end: true,
  label: "Dashboard",
  icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
};
const LINK_CODES = {
  to: "/codes",
  label: "QR Codes",
  icon: "M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z",
};
const LINK_ANALYTICS = {
  to: "/analytics",
  label: "Analytics",
  icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
};
const LINK_IMPORT = {
  to: "/batch",
  label: "Import",
  icon: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12",
};
const LINK_EXPORT = {
  to: "/export",
  label: "Export",
  icon: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4",
};
const LINK_ORG = {
  to: "/settings/org",
  label: "Organisation",
  icon: "M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-3-6.65",
};
const LINK_SUPPORT = {
  to: "/tickets",
  label: "Support",
  icon: "M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7z",
};
// Owner/admin-only links (hidden from plain members).
const LINK_API_KEYS = {
  to: "/settings/api-keys",
  label: "API Keys",
  lockFeature: "API access",
  icon: "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z",
};
const LINK_BILLING = {
  to: "/billing",
  label: "Billing & plan",
  icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
};

/**
 * Build the grouped sidebar IA (PRIVATE-AREA-PLAN §3 / PA4): Workspace (content)
 * / Tools / Account. API keys + Billing stay owner/admin-only. (Webhooks stays
 * under /settings — surfacing it here would jump into the settings shell.)
 */
function buildNavGroups(role) {
  const isAdmin = role === "owner" || role === "admin";
  return [
    { title: "Workspace", links: [LINK_DASHBOARD, LINK_CODES, LINK_ANALYTICS] },
    { title: "Tools", links: [LINK_IMPORT, LINK_EXPORT] },
    {
      title: "Account",
      links: [
        LINK_ORG,
        ...(isAdmin ? [LINK_API_KEYS, LINK_BILLING] : []),
        LINK_SUPPORT,
      ],
    },
  ];
}

/** One row inside the account menu (router link, active-state aware). */
function AccountMenuItem({ to, icon: ItemIcon, children }) {
  return (
    <Menu.Item>
      {({ active }) => (
        <Link
          to={to}
          className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm ${
            active ? "bg-gray-100 text-gray-900" : "text-gray-700"
          }`}
        >
          <ItemIcon className="h-4 w-4 text-gray-400" />
          {children}
        </Link>
      )}
    </Menu.Item>
  );
}
AccountMenuItem.propTypes = {
  to: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node,
};
AccountMenuItem.defaultProps = { children: null };

const sideNavClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
    isActive
      ? "bg-brand-50 text-brand-700"
      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
  }`;

const bottomNavClass = ({ isActive }) =>
  `flex flex-col items-center gap-0.5 px-3 py-2 text-xs font-medium transition-colors ${
    isActive ? "text-brand-700" : "text-gray-500 hover:text-gray-900"
  }`;

/**
 * Layout — persistent shell for all authenticated pages.
 *
 * Desktop (sm+): fixed sidebar on left, topbar with email/logout.
 * Mobile (<sm): topbar only; bottom nav bar pinned to viewport bottom.
 */
function Layout() {
  const { user, logout, currentOrg } = useAuth();

  // <main> is the scroll region in the app-shell, so reset *its* scroll (not the
  // window's) to the top whenever the route changes.
  const mainRef = React.useRef(null);
  const { pathname } = useLocation();
  React.useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [pathname]);

  // Prefer the display name; fall back to the email when it is unset.
  const displayName = user?.display_name || user?.email;

  // Current plan id — drives lock chips on sub-plan nav items. Best-effort: a
  // failed/absent fetch just means no chips (never breaks the shell).
  const [plan, setPlan] = React.useState(null);
  React.useEffect(() => {
    let active = true;
    getUsage()
      .then((d) => active && setPlan(planId(d?.plan?.name)))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [currentOrg?.id]);

  // Grouped sidebar IA; API keys + Billing shown to owner/admin only.
  const navGroups = useMemo(
    () => buildNavGroups(currentOrg?.role),
    [currentOrg?.role],
  );
  // Flat list (Workspace + Tools) for the mobile bottom bar — Account items are
  // reachable via the topbar account menu, so we keep the bar to a few essentials.
  const mobileLinks = useMemo(
    () => navGroups.filter((g) => g.title !== "Account").flatMap((g) => g.links),
    [navGroups],
  );

  /** Tier label for a nav item's lock chip, or null when unlocked/unknown. */
  const lockTier = (link) => {
    if (!link.lockFeature || !plan || hasFeature(plan, link.lockFeature)) {
      return null;
    }
    return tierForFeature(link.lockFeature)?.name || "Pro";
  };

  // authApi.logout (via context) revokes the session server-side and redirects.
  const handleLogout = () => {
    logout();
  };

  return (
    // App-shell: the shell is exactly viewport-tall and never scrolls itself;
    // the sidebar + topbar stay fixed and only <main> scrolls (see min-h-0).
    <div className="h-dvh flex bg-gray-50 overflow-hidden">
      {/* ── Sidebar (desktop only) ────────────────────────────────────── */}
      <aside className="hidden sm:flex flex-col w-56 shrink-0 bg-white border-r border-gray-200">
        {/* Logo — shared with the public site; links to the dashboard. */}
        <div className="h-14 flex items-center px-4 border-b border-gray-200">
          <Link to="/dashboard" aria-label="QRFlow dashboard">
            <Logo className="h-7" />
          </Link>
        </div>

        {/* Grouped nav (Workspace / Tools / Account) + folder tree */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {navGroups.map((group) => (
            <div key={group.title} className="space-y-1">
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                {group.title}
              </p>
              {group.links.map((link) => {
                const { to, end, label, icon } = link;
                const tier = lockTier(link);
                return (
                  <NavLink key={to} to={to} end={end} className={sideNavClass}>
                    <Icon path={icon} />
                    <span className="flex-1">{label}</span>
                    {tier && <LockChip tier={tier} />}
                  </NavLink>
                );
              })}
              {/* Folder navigator sits under Workspace — it filters /codes (P14-G8). */}
              {group.title === "Workspace" && <FolderTree />}
            </div>
          ))}

          {/* Help / Docs → public docs (new tab) */}
          <div className="space-y-1">
            <a
              href="/docs"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              <Icon path="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              <span className="flex-1">Help &amp; Docs</span>
            </a>
          </div>
        </nav>

        {/* Admin panel entry — superadmins only. Indigo accent matches the
            admin shell's theme so the destination is visually signposted. */}
        {user?.is_superadmin && (
          <div className="px-3 py-4 border-t border-gray-200">
            <Link
              to="/admin"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
            >
              <Icon path="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              Admin Panel
            </Link>
          </div>
        )}
      </aside>

      {/* ── Main column ───────────────────────────────────────────────── */}
      {/* min-h-0 lets the flex child shrink so <main> (not the page) scrolls. */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Topbar */}
        <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 shrink-0">
          {/* Left: mobile logo + org switcher */}
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/dashboard" className="sm:hidden" aria-label="QRFlow dashboard">
              <Logo className="h-6" />
            </Link>
            <OrgSwitcher />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Persistent plan badge + Upgrade — visible on every app page. */}
            <PlanBadge showUpgrade />

            {/* Mobile-only admin entry (the sidebar link is hidden < sm). */}
            {user?.is_superadmin && (
              <Link
                to="/admin"
                className="sm:hidden text-xs font-bold uppercase tracking-wide text-indigo-600 hover:text-indigo-500"
              >
                Admin
              </Link>
            )}
            <NotificationBell />

            {/* Account menu — initials avatar → account / org / billing / logout. */}
            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center gap-2 rounded-lg px-1.5 py-1.5 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                  {initials(displayName)}
                </span>
                <span className="hidden sm:block max-w-[160px] truncate text-sm text-gray-700">
                  {displayName}
                </span>
                <ChevronDownIcon className="hidden sm:block h-4 w-4 text-gray-400" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-40 mt-2 w-56 origin-top-right rounded-xl bg-white p-1.5 shadow-lg ring-1 ring-gray-200 focus:outline-none">
                  <div className="border-b border-gray-100 px-3 py-2">
                    <p className="truncate text-sm font-medium text-gray-900">{displayName}</p>
                    {user?.email && (
                      <p className="truncate text-xs text-gray-500">{user.email}</p>
                    )}
                  </div>
                  <div className="py-1">
                    <AccountMenuItem to="/settings" icon={UserCircleIcon}>
                      Account settings
                    </AccountMenuItem>
                    <AccountMenuItem to="/settings/org" icon={BuildingOffice2Icon}>
                      Organisation
                    </AccountMenuItem>
                    <AccountMenuItem to="/billing" icon={CreditCardIcon}>
                      Billing &amp; plan
                    </AccountMenuItem>
                  </div>
                  <div className="border-t border-gray-100 py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm ${
                            active ? "bg-red-50 text-red-600" : "text-gray-700"
                          }`}
                        >
                          <ArrowRightOnRectangleIcon className="h-4 w-4 text-gray-400" />
                          Log out
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </header>

        {/* Page content — the only scroll region; extra bottom padding on
            mobile clears the fixed bottom nav. */}
        <main ref={mainRef} className="flex-1 min-h-0 overflow-y-auto p-6 pb-20 sm:pb-6">
          <Outlet />
        </main>
      </div>

      {/* ── Bottom nav (mobile only) — Workspace + Tools essentials; Account
           items live in the topbar account menu. ──────────────────────── */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 flex">
        {mobileLinks.map(({ to, end, label, icon }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={bottomNavClass}
            style={{ flex: 1 }}
          >
            <Icon path={icon} className="w-5 h-5" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Floating agent assistant — hidden on /admin/* (handled internally). */}
      <ChatWidget />

      {/* Blocks the app to re-collect consent after a policy version bump. */}
      <ReConsentPrompt />
    </div>
  );
}

export default Layout;
